import { Link } from 'react-router-dom';
import type { AdventureSummary } from '../../services/GameApiService';
import AdventureModeBadge from './AdventureModeBadge';
import AdventureSocialProofBadge from './AdventureSocialProofBadge';

interface AdventureCardProps {
  adventure: AdventureSummary;
}

/**
 * Card visual usado na grelha do catálogo público.
 * Mostra hero image (com fallback para gradiente temático), título, tema,
 * nº de episódios e um badge com o modo de jogo (História / Mistério).
 */
export default function AdventureCard({ adventure }: AdventureCardProps) {
  const initial = adventure.initialCharacter;

  // Prefer the slug for pretty URLs (`/adventure/a-setima-arca`); fall back to
  // the raw id for adventures that predate the slug field.
  const adventurePath = adventure.slug
    ? `/adventure/${adventure.slug}`
    : `/adventure/${adventure.id}`;

  return (
    <Link
      to={adventurePath}
      className="group block bg-white rounded-2xl shadow-md hover:shadow-2xl overflow-hidden transition-all duration-300 transform hover:-translate-y-1"
    >
      <div className="relative h-56 md:h-64 overflow-hidden bg-gradient-to-br from-dark via-slate-700 to-primary">
        {adventure.heroImageUrl ? (
          <img
            src={adventure.heroImageUrl}
            alt={adventure.title}
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
            loading="lazy"
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center text-white/60">
            <span className="text-sm uppercase tracking-widest">
              {adventure.theme || 'Aventura'}
            </span>
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-dark/95 via-dark/30 to-transparent" />

        {/* Gameplay-mode badge (replaces the previous initial-character avatar). */}
        <AdventureModeBadge mode={adventure.mysteryMode} variant="circle" />

        {/* Featured badge: top-left corner, only when adventure is featured */}
        {adventure.featured && (
          <div className="absolute top-4 left-4 px-3 py-1 text-xs font-bold uppercase tracking-wider rounded-full bg-amber-500 text-white shadow-lg">
            Em destaque
          </div>
        )}

        {/* Top-right chip: episode count */}
        {adventure.totalEpisodes != null && (
          <div className="absolute top-4 right-4 px-3 py-1 text-xs font-bold uppercase tracking-wider rounded-full bg-black/40 text-white backdrop-blur-sm">
            {adventure.totalEpisodes} episódios
          </div>
        )}
      </div>

      <div className="p-5">
        {adventure.theme && (
          <p className="text-xs font-semibold uppercase tracking-wider text-primary mb-1">
            {adventure.theme}
          </p>
        )}
        <h3 className="text-xl md:text-2xl font-bold text-dark mb-2 line-clamp-2 group-hover:text-primary transition-colors">
          {adventure.title}
        </h3>

        {adventure.synopsis && (
          <p className="text-sm text-gray-600 leading-relaxed line-clamp-3 mb-3">
            {adventure.synopsis}
          </p>
        )}

        {adventure.criticQuoteShort && (
          <div className="mb-3 border-l-4 border-accent pl-3">
            <p className="text-sm italic text-gray-700">
              "{adventure.criticQuoteShort}"
            </p>
          </div>
        )}

        <div className="mb-3">
          <AdventureSocialProofBadge
            adventureId={adventure.id}
            completedCount={adventure.completedCount}
            variant="card"
          />
        </div>

        {initial && (
          <p className="text-xs text-gray-500 italic">
            Começa com <span className="font-semibold text-dark">{initial.name}</span>
            {initial.role ? `, ${initial.role}` : ''}
          </p>
        )}
      </div>
    </Link>
  );
}